<template>
  <div class="modal-overlay top settings-backdrop" @click.self="$emit('close')">
    <div class="settings-modal" role="dialog" aria-modal="true" aria-label="Settings">
      <button class="close-btn" @click="$emit('close')" aria-label="Close">×</button>

      <h1>Luxor's Wiznaib.us</h1>
      <p class="blurb">
        A unit-stats viewer for <a href="https://twitch.tv/fftbattleground" target="_blank" rel="noopener">FFTBG</a>
        — the Final Fantasy Tactics tournament bot on Twitch. Thanks to thekillernacho for running it.
      </p>

      <section>
        <h3>Who are you?</h3>
        <p class="hint">
          Your Twitch username — turns the top bar's Log in into a 👤 button for quick access
          to your player profile, and highlights your name on the bracket.
        </p>
        <div class="who-row">
          <input type="text" v-model="local.myUsername" placeholder="username" maxlength="30" class="my-username-input">
          <input type="color" v-model="local.myColor" :title="'Highlight color for ' + (local.myUsername || 'you')">
        </div>
        <div class="twitch-row">
          <template v-if="twitchName">✓ Logged in with Twitch as <b>{{ twitchName }}</b> · <button class="link-btn" @click="logoutTwitch">log out</button></template>
          <template v-else>
            <button class="twitch-login" @click="loginTwitch">Log in with Twitch</button>
            <span class="twitch-note">or type it above. Twitch fills it in for you; the site only learns your name.</span>
          </template>
        </div>
      </section>

      <section>
        <h3>Display</h3>
        <label class="checkbox-row">
          <input type="checkbox" v-model="local.hideBracket"> Hide bracket section
        </label>
        <label class="checkbox-row">
          <input type="checkbox" v-model="local.showTicker"> Show the 🎉 "Just happened" strip (recent titles, quests and long reigns)
        </label>
        <label class="checkbox-row">
          <input type="checkbox" v-model="showAnnouncements">
          Show big "X is in! / X was Snubbed!" callout at start of each tournament
        </label>
        <label class="checkbox-row">
          <input type="checkbox" v-model="local.showWinRates">
          Color skills &amp; gear by win rate (green = above average, red = below)
        </label>
        <label class="checkbox-row">
          <input type="checkbox" v-model="local.attackAnims">
          Live-match fighters occasionally play their attack animation
        </label>
      </section>

      <section>
        <h3>Sounds</h3>
        <p class="hint">
          Optional notification sounds for your <b>highlighted users</b> (above) — a chime when
          their match comes up, when it's won or lost, and a fanfare if they champ.
        </p>
        <label class="sound-set-row">
          Sound set
          <select v-model="local.soundSet">
            <option v-for="(s, id) in soundSets" :key="id" :value="id">{{ s.label }}</option>
          </select>
        </label>
        <div class="sound-volume-row">
          <span class="sv-label">Volume</span>
          <input type="range" min="0" max="200" step="5" v-model.number="volumePct" aria-label="Notification volume">
          <span class="sv-value">{{ volumePct }}%</span>
          <button type="button" class="sound-test" @click.prevent="testSound('newTournament')" title="Preview at this volume">▶</button>
        </div>
        <label v-for="ev in soundEventList" :key="ev.key" class="checkbox-row sound-row">
          <input type="checkbox" v-model="local.soundEvents[ev.key]">
          <span class="sound-desc">{{ ev.label }}</span>
          <button type="button" class="sound-test" @click.prevent="testSound(ev.key)" title="Preview">▶</button>
        </label>
      </section>

      <section>
        <h3>Highlighted users</h3>
        <p class="hint">
          Names listed here get a callout ("X is in!" / "X was Snubbed!") when they enter
          or get snubbed from a tournament.
        </p>
        <label class="checkbox-row">
          <input type="checkbox" v-model="showDefaults">
          Show default highlighted players (luxordoesntframe, yoimbear, firstodie)
        </label>

        <div class="custom-users">
          <div v-for="(user, i) in local.customHighlights" :key="i" class="custom-row">
            <input type="text" v-model="user.name" placeholder="username" maxlength="30">
            <input type="color" v-model="user.color" :title="'Color for ' + (user.name || 'this user')">
            <button class="remove-btn" @click="removeUser(i)" aria-label="Remove">×</button>
          </div>
          <button class="add-btn" @click="addUser">+ Add user</button>
        </div>
      </section>

      <section class="footer">
        <a href="?intro" class="nav-btn">🎓 Replay intro tour</a>
        <a href="?changelog" class="nav-btn">📜 What's new</a>
        <button class="reset-btn" @click="reset">Reset all settings</button>
      </section>
    </div>
  </div>
</template>

<script>
import modalA11y from '../mixins/modalA11y.js';
import { startTwitchLogin, twitchAuth, twitchLogout } from '../lib/twitchAuth.js';
import { SOUND_SETS, DEFAULT_SOUND_SET, playEvent } from '../lib/sounds.js';

const SOUND_EVENT_LIST = [
  { key: 'newTournament', label: 'New tournament starting (so you can enter)' },
  { key: 'matchUp', label: "A highlighted user's match is coming up" },
  { key: 'win', label: 'A highlighted user won their match' },
  { key: 'loss', label: 'A highlighted user lost their match' },
  { key: 'champ', label: 'A highlighted user won the championship 🏆' },
];

export default {
  mixins: [modalA11y],
  props: {
    // Current prefs object — we mutate a local copy and emit on changes.
    prefs: { type: Object, required: true },
  },

  emits: ['update:prefs', 'close'],

  data() {
    // Defensive clone so the parent's prefs object isn't mutated directly —
    // we emit on every change via the deep watcher below.
    return {
      local: {
        ...this.prefs,
        customHighlights: this.prefs.customHighlights.map(u => ({ ...u })),
        soundEvents: { ...(this.prefs.soundEvents || {}) },
      },
      soundSets: SOUND_SETS,
      twitchName: (twitchAuth() || {}).login || '',
      soundEventList: SOUND_EVENT_LIST,
    };
  },

  computed: {
    // Inverse of hideDefaultHighlights — the checkbox in the UI is phrased
    // positively ("Show default …") which reads better than a "Hide" toggle.
    showDefaults: {
      get()  { return !this.local.hideDefaultHighlights; },
      set(v) { this.local.hideDefaultHighlights = !v; },
    },
    // Same shape, inverse of disableAnnouncements.
    showAnnouncements: {
      get()  { return !this.local.disableAnnouncements; },
      set(v) { this.local.disableAnnouncements = !v; },
    },
    // Volume slider works in percent (0–200); the pref is a 0–2 multiplier.
    volumePct: {
      get()  { return Math.round((this.local.soundVolume ?? 1) * 100); },
      set(v) { this.local.soundVolume = Math.max(0, Math.min(2, (Number(v) || 0) / 100)); },
    },
  },

  watch: {
    local: {
      deep: true,
      handler(newVal) { this.$emit('update:prefs', JSON.parse(JSON.stringify(newVal))); },
    },
  },

  methods: {
    loginTwitch() { startTwitchLogin(); },
    // Logging out forgets the Twitch login and, if it set it, the username too.
    logoutTwitch() {
      if (this.local.myUsername.toLowerCase() === this.twitchName.toLowerCase()) this.local.myUsername = '';
      twitchLogout();
      this.twitchName = '';
    },
    addUser() {
      this.local.customHighlights.push({ name: '', color: '#ffd700' });
    },
    removeUser(i) {
      this.local.customHighlights.splice(i, 1);
    },
    // Preview a sound from the currently-selected set (plays even if the event's
    // toggle is off, so you can audition before enabling).
    testSound(ev) {
      playEvent(this.local.soundSet || DEFAULT_SOUND_SET, ev, this.local.soundVolume);
    },
    reset() {
      if (!window.confirm('Reset all settings to defaults? (Also clears the "tournaments already announced" history.)')) return;
      this.local = {
        hideDefaultHighlights: false,
        hideBracket: false,
        customHighlights: [],
        disableAnnouncements: false,
        soundSet: DEFAULT_SOUND_SET,
        soundEvents: { newTournament: false, matchUp: false, win: false, loss: false, champ: false },
        soundVolume: 1,
        expandedCards: false,
        showWinRates: false,
        attackAnims: true,
        showTicker: true,
        myUsername: '',
        myColor: '#ffd700',
        seenAnnouncements: [],
      };
    },
  },
};
</script>

<style scoped>
.settings-backdrop {
  background: rgba(0, 0, 0, 0.6);
  padding: 40px 16px;
}
.settings-modal {
  position: relative;
  width: 100%;
  max-width: 560px;
  /* the 2026-10 refresh's brass-framed navy panel (assets/css/theme.scss) */
  background:
    linear-gradient(var(--frame-hi), var(--frame-hi)) left 0 top 0 / 14px 2px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) left 0 top 0 / 2px 14px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) right 0 top 0 / 14px 2px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) right 0 top 0 / 2px 14px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) left 0 bottom 0 / 14px 2px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) left 0 bottom 0 / 2px 14px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) right 0 bottom 0 / 14px 2px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) right 0 bottom 0 / 2px 14px no-repeat,
    linear-gradient(180deg, var(--panel-top), var(--panel-bottom));
  border: 1px solid var(--brass-edge);
  border-radius: 6px;
  box-shadow: 0 16px 50px rgba(0, 0, 0, 0.6);
  color: var(--ink);
  padding: 28px 32px 24px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 14px;
}
.close-btn {
  position: absolute;
  top: 6px;
  right: 10px;
  background: transparent;
  border: none;
  color: var(--mute);
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  padding: 4px 8px;
  font-family: inherit;
}
.close-btn:hover { color: var(--brass-hi); }

.settings-modal h1 {
  font-family: var(--display);
  font-weight: 700;
  font-size: 28px;
  text-align: center;
  margin: 0 0 6px;
  background: linear-gradient(#fff3cf, #e2bd6a 55%, #a27a35); -webkit-background-clip: text; background-clip: text; color: transparent;
}
.blurb {
  text-align: center;
  font-size: 13px;
  line-height: 1.4;
  margin: 0 0 20px;
  opacity: 0.85;
}
.blurb a { color: var(--brass-hi); }

section {
  margin: 16px 0;
  padding-top: 12px;
  border-top: 1px solid var(--line);
}
section h3 {
  font-family: var(--cond);
  font-weight: 700;
  font-size: 15px;
  letter-spacing: 1.8px;
  text-transform: uppercase;
  color: var(--brass);
  margin: 0 0 8px;
}
.hint {
  font-size: 12px;
  opacity: 0.7;
  margin: 0 0 10px;
  line-height: 1.35;
}

.nav-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
}
.twitch-row { margin-top: 8px; font-size: 13px; display: flex; flex-wrap: wrap; align-items: center; gap: 6px 8px; }
.twitch-login { color: #1a1408; background: linear-gradient(#e8cf8c, #9c7a3a); border: 0; padding: 4px 14px; font-family: var(--cond); font-size: 15px; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase; cursor: pointer; clip-path: polygon(7px 0, 100% 0, calc(100% - 7px) 100%, 0 100%); }
.twitch-login:hover { filter: brightness(1.12); }
.twitch-note { font-size: 12px; opacity: 0.7; }
.link-btn { background: none; border: 0; padding: 0; color: inherit; text-decoration: underline; cursor: pointer; font: inherit; }
.who-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.my-username-input {
  flex: 1;
  max-width: 220px;
  background: var(--navy2);
  border: 1px solid var(--brass-edge);
  color: var(--ink-hi);
  padding: 5px 8px;
  font-family: inherit;
  font-size: 13px;
}
.who-row input[type=color] {
  width: 32px;
  height: 26px;
  border: 1px solid var(--brass-edge);
  background: transparent;
  cursor: pointer;
  padding: 0;
}
.nav-btn {
  display: inline-block;
  background: transparent;
  color: var(--brass);
  border: 1px solid var(--brass-edge);
  border-radius: 3px;
  padding: 3px 10px;
  font-family: var(--cond);
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  cursor: pointer;
  text-decoration: none;
}
.nav-btn:hover { background: rgba(217, 180, 94, 0.12); }

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  cursor: pointer;
  margin: 6px 0;
  line-height: 1.3;
}
.checkbox-row input { cursor: pointer; }

.sound-set-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  margin: 2px 0 8px;
}
.sound-set-row select {
  background: var(--navy2);
  border: 1px solid var(--brass-edge);
  color: var(--ink-hi);
  padding: 3px 6px;
  font-family: inherit;
  font-size: 13px;
}
.sound-volume-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  margin: 2px 0 10px;
}
.sound-volume-row .sv-label { flex: 0 0 auto; }
.sound-volume-row input[type="range"] { flex: 1; min-width: 80px; accent-color: var(--brass); cursor: pointer; }
.sound-volume-row .sv-value { flex: 0 0 auto; width: 40px; text-align: right; font-variant-numeric: tabular-nums; opacity: 0.85; }
.sound-row .sound-desc { flex: 1; }
.sound-test {
  background: transparent;
  border: 1px solid var(--brass-edge);
  color: var(--brass);
  border-radius: 3px;
  width: 24px;
  height: 22px;
  font-size: 11px;
  line-height: 1;
  cursor: pointer;
  flex: 0 0 auto;
  padding: 0;
}
.sound-test:hover { background: rgba(217, 180, 94, 0.15); color: var(--brass-hi); }

.custom-users { margin-top: 8px; }
.custom-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 4px 0;
}
.custom-row input[type=text] {
  flex: 1;
  background: var(--navy2);
  border: 1px solid var(--brass-edge);
  color: var(--ink-hi);
  padding: 4px 8px;
  font-family: inherit;
  font-size: 13px;
}
.custom-row input[type=color] {
  width: 32px;
  height: 26px;
  border: 1px solid var(--brass-edge);
  background: transparent;
  cursor: pointer;
  padding: 0;
}
.remove-btn {
  background: transparent;
  border: 1px solid var(--brass-edge);
  color: var(--ink);
  width: 26px;
  height: 26px;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  font-family: inherit;
}
.remove-btn:hover { background: #8e2b22; border-color: #e07a6e; }

.add-btn {
  margin-top: 6px;
  background: transparent;
  border: 1px dashed var(--brass-edge);
  color: var(--brass);
  padding: 4px 12px;
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
}
.add-btn:hover { background: rgba(217, 180, 94, 0.1); }

.footer { text-align: center; border-top: 1px solid var(--line); display: flex; gap: 12px; justify-content: center; align-items: center; flex-wrap: wrap; }
.reset-btn {
  background: transparent;
  border: 1px solid #e74c3c;
  color: #e74c3c;
  padding: 5px 16px;
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
}
.reset-btn:hover { background: #e74c3c; color: #fff; }
</style>
